import type { UserDTO, WorkStatsDTO, WorkStatsScope } from '@wberp/shared';
import { api } from './api';

export type StatsState = { data: WorkStatsDTO | null; loading: boolean; error: boolean };
type Entry = { state: StatsState; fetched: number; pending: Promise<void> | null; listeners: Set<() => void> };
type StatsKey = { scope: WorkStatsScope; companyId?: string };
export const STATS_POLL_MS = 30_000;

// Memory only, isolated by login identity. Never put company statistics on disk.
export class WorkStatsStore {
  private entries = new Map<string, Entry>();
  private identity = '';
  private generation = 0;
  constructor(private readonly fetchStats: (scope: WorkStatsScope, companyId?: string) => Promise<WorkStatsDTO>, private readonly minimumGap = 1000) {}

  setIdentity(user: UserDTO | null): void {
    const key = user ? JSON.stringify([user.companyId, user.role, user.employeeId ?? null, user.id]) : '';
    if (key === this.identity) return;
    this.identity = key; this.generation++;
    for (const entry of this.entries.values()) {
      entry.state = { data: null, loading: false, error: false };
      for (const listener of entry.listeners) listener();
    }
    this.entries.clear();
  }
  private key({ scope, companyId }: StatsKey): string { return JSON.stringify([scope, companyId ?? null]); }
  private entry(key: StatsKey): Entry {
    const entryKey = this.key(key);
    let entry = this.entries.get(entryKey);
    if (!entry) {
      entry = { state: { data: null, loading: false, error: false }, fetched: 0, pending: null, listeners: new Set() };
      this.entries.set(entryKey, entry);
    }
    return entry;
  }
  peek(scope: WorkStatsScope, companyId?: string): StatsState { return this.entry({ scope, companyId }).state; }
  subscribe(scope: WorkStatsScope, listener: () => void, companyId?: string): () => void {
    const entry = this.entry({ scope, companyId }); entry.listeners.add(listener);
    return () => { entry.listeners.delete(listener); };
  }
  load(scope: WorkStatsScope, force = false, companyId?: string): Promise<void> {
    if (!this.identity) return Promise.resolve();
    const entry = this.entry({ scope, companyId });
    if (entry.pending) return entry.pending;
    if (!force && entry.state.data && Date.now() - entry.fetched < STATS_POLL_MS) return Promise.resolve();
    const generation = this.generation;
    const publish = (state: StatsState) => {
      if (this.generation !== generation) return;
      entry.state = state;
      for (const listener of entry.listeners) listener();
    };
    publish({ ...entry.state, loading: !entry.state.data, error: false });
    entry.pending = (async () => {
      const delay = Math.max(0, this.minimumGap - (Date.now() - entry.fetched));
      if (delay) await new Promise(resolve => setTimeout(resolve, delay));
      if (this.generation !== generation) return;
      try {
        const data = await this.fetchStats(scope, companyId);
        if (this.generation === generation) entry.fetched = Date.now();
        publish({ data, loading: false, error: false });
      } catch { publish({ ...entry.state, loading: false, error: true }); }
      finally { entry.pending = null; }
    })();
    return entry.pending;
  }
}

export const workStatsStore = new WorkStatsStore((scope, companyId) => api.getWorkStats(scope, companyId));
